import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog}                               from '@angular/material/dialog';
import {MatSidenav}                              from '@angular/material/sidenav';
import {MatSnackBar}                             from '@angular/material/snack-bar';
import {ActivatedRoute, Router}                  from '@angular/router';
import {PurchaseCreditsComponent}                from '@src/app/admin/settings/components/purchase-credits/purchase-credits.component';
import {ImpersonateDialogComponent}              from '@src/app/admin/users/components/impersonate-dialog/impersonate-dialog.component';
import {CreditsService}                          from '@src/app/admin/settings/services/credits.service';
import {UserPreferencesComponent}                from '@src/app/admin/users/components/user-prefereces/user-preferences.component';
import {UsersService}                            from '@src/app/admin/users/services/users.service';
import {AuthService}                             from '@src/app/core/services/auth.service';
import {FeatureFlagService}                      from '@src/app/core/services/feature-flag.service';
import {GlobalService}                           from '@src/app/core/services/global.service';
import {ImpersonationService}                    from '@src/app/core/services/impersonation.service';
import {MaintenanceSummaryService}               from '@src/app/core/services/maintenance-summary.service';
import {PermissionsService}                      from '@src/app/core/services/permissions.service';
import {MarketingConsentModalComponent}          from '@src/app/shared/components/marketing-consent-modal/marketing-consent-modal.component';
import {PusherService}                           from '@src/app/shared/services/pusher.service';
import {ThemeService}                            from '@src/app/shared/services/theme.service';
import {ApiService}                              from '@src/app/shared/services/api.service';
import {WalkthroughService}                      from '@src/app/shared/services/walkthrough/walkthrough.service';
import {environment}                             from '@src/environments/environment';
import {DeviceDetectorService}                   from 'ngx-device-detector';
import {Subscription, timeout}                   from 'rxjs';
import Swal                                      from 'sweetalert2';

@Component({
    standalone: false,
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy
{
    @ViewChild('sidenav', {static: false}) sideNav: MatSidenav;
    public currentTheme: string = '';
    public title: string = '';
    public pageTitle: string = '';
    public links: any = [
        {name: 'Dashboard', url: 'app', icon: 'home'},
        {name: 'Sites', url: 'app/sites', icon: 'room', permission: 'view_sites'},
        {name: 'Maintenance', url: 'app/maintenance', icon: 'event_repeat', permission: 'view_maintenance', featureFlag: 'maintenance_reminders'},
        {name: 'Reports', url: 'app/reports', icon: 'bar_chart', permission: ['view_reports', 'view_visit_reports']},
        {name: 'Timesheets', url: 'app/timesheets', icon: 'history', permission: 'view_timesheets'},
        {name: 'Clients', url: 'app/customers', icon: 'business', permission: 'view_customers'},
        {name: 'Products', url: 'app/products', icon: 'category', permission: 'view_products'},
        {name: 'Defects', url: 'app/defects', icon: 'warning', permission: 'view_defects'},
        {
            name: 'Manufacturers',
            url: 'app/manufacturers',
            icon: 'precision_manufacturing',
            permission: 'view_manufacturers'
        },
        {name: 'Forms', url: 'app/forms', icon: 'build', featureFlag: 'form_builder'},
        {name: 'Users', url: 'app/users', icon: 'people', permission: 'view_users'},
        {name: 'Settings', url: 'app/settings/0', icon: 'settings', permission: ['view_settings', 'manage_master_data', 'add_forms', 'edit_forms']},
    ];

    public featureFlags: Record<string, boolean> = {
        form_builder: false,
        maintenance_reminders: false,
    };
    private featureFlagSubscriptions = new Subscription();
    private maintenanceSummarySubscription?: Subscription;
    private marketingConsentPromptTimeoutId: number | null = null;

    public constructor(
        public dialog: MatDialog,
        public usersService: UsersService,
        public authService: AuthService,
        public impersonationService: ImpersonationService,
        public pusherService: PusherService,
        public walkthroughService: WalkthroughService,
        public themeService: ThemeService,
        public permissionsService: PermissionsService,
        private router: Router,
        private featureFlagService: FeatureFlagService,
        private globalService: GlobalService,
        private creditsService: CreditsService,
        private activatedRoute: ActivatedRoute,
        private snackBar: MatSnackBar,
        private deviceService: DeviceDetectorService,
        private apiService: ApiService,
        private maintenanceSummaryService: MaintenanceSummaryService,
    )
    {
        this.title = environment.title;
        this.pageTitle = this.globalService.title

        this.featureFlagService.initialize(this.usersService.user, this.authService.tenant).then(() => {
            ['form_builder', 'maintenance_reminders'].forEach(flag => {
                this.featureFlagSubscriptions.add(this.featureFlagService.getFlag(flag).subscribe((value) => {
                    this.featureFlags[flag] = Boolean(value);
                    if (flag === 'maintenance_reminders' && Boolean(value)) this.loadMaintenanceSummary();
                }));
            });
        }).catch(() => {
            this.featureFlags.form_builder = false;
            this.featureFlags.maintenance_reminders = false;
        });

        this.activatedRoute.queryParams.subscribe(params => {
            if (params?.session_id) {
                this.snackBar.open('Thank you for your purchase! You have successfully purchased credits.', 'Ok', {
                    duration: 2000,
                });
                let newParams = {...params};
                delete newParams.session_id;
                this.router.navigate([], {queryParams: newParams});
            }
        });

        if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
            Swal.fire({
                title: 'For the best expereince please use a laptop or desktop device',
                icon: 'warning',
                showCancelButton: false,
                confirmButtonText: 'Ok'
            });
        }
    }

    public ngOnInit(): void
    {
        this.themeService.theme.subscribe(theme => {
            this.currentTheme = theme;
        });

        this.scheduleMarketingConsentPrompt();
        this.maintenanceSummarySubscription = this.maintenanceSummaryService.refreshRequested$.subscribe(() => {
            if (this.featureFlags.maintenance_reminders) this.loadMaintenanceSummary();
        });

        this.creditsService.getCreditsBalance().subscribe(
            response => this.authService.credits = response.data.balance,
            error => this.creditsService.handleError(error)
        );

        const channel = this.pusherService.listen(`credits.${this.authService.tenant.id}`);
        channel.bind('balance.updated', (data) => {
            this.authService.credits = data.balance;
        });

        const currentUrl = this.router.url;

        if (currentUrl === '/app' || currentUrl === '/app/') {
            this.walkthroughService.start('dashboard', false);
        }
    }

    public logout(): void
    {
        this.authService.logout();
        this.router.navigate(['/login']).then(r => r);
    }

    private loadMaintenanceSummary(): void
    {
        if (!this.permissionsService.can('view_maintenance')) {
            return;
        }

        this.apiService.get('dashboard/maintenance-due', {window_days: 30, per_page: 1}).subscribe({
            next: response => {
                const maintenanceLink = this.links.find(link => link.url === 'app/maintenance');
                if (maintenanceLink) {
                    maintenanceLink.badge = Number(response?.meta?.total ?? 0);
                }
            },
            error: () => undefined,
        });
    }

    public toggleTheme(): void
    {
        this.themeService.toggleTheme();
    }

    public openUserPreferences(): void
    {
        this.dialog.open(UserPreferencesComponent, {
            width: '50%',
            panelClass: 'mobile-width',
            disableClose: true,
        });
    }

    public openImpersonateDialog(): void
    {
        const impersonateDialog = this.dialog.open(ImpersonateDialogComponent, {
            width: '40%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {
                currentUserId: this.usersService.user?.id ?? null,
            },
        });

        impersonateDialog.afterClosed().subscribe(result => {
            if (!result?.userId) {
                return;
            }

            this.impersonationService.impersonate(result.userId).subscribe({
                next: response => {
                    this.impersonationService.startSession(response);
                    window.location.reload();
                },
                error: error => {
                    this.snackBar.open(this.getImpersonationErrorMessage(error, 'Unable to start impersonation.'), 'Ok', {
                        duration: 5000,
                    });
                },
            });
        });
    }

    public stopImpersonating(): void
    {
        if (!this.impersonationService.isImpersonating) {
            return;
        }

        const impersonationToken = this.authService.accessToken;
        const finishStop = (): void => {
            this.impersonationService.endSession();
            this.router.navigate(['/app']).then(() => window.location.reload());
        };

        this.impersonationService.stop(impersonationToken).pipe(timeout(5000)).subscribe({
            next: () => finishStop(),
            error: error => {
                if (error?.status !== 400) {
                    console.warn('Impersonation stop API call failed:', error?.status, error?.error?.message);
                }
                finishStop();
            },
        });
    }

    // TODO: add sweet alert to confirm loss of any unsaved data when switching tenant
    // TODO: Create a tenant type
    public switchTenant(tenant): void
    {
        this.authService.tenant = tenant;
        this.router.navigate(['/app/sites']);
        window.location.reload();
    }

    public purchaseCredits()
    {
        const purchaseCreditModal = this.dialog.open(PurchaseCreditsComponent, {
            width: '60%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: null,
        });
    }

    public openExternalLink(location: string): void
    {
        let tab: Window;

        switch (location) {
            case 'help':
                window.open('https://firesurveypro.co.uk/docs', '_blank');
                break;

            case 'ticket':
                window.open('https://vulcan-systems.atlassian.net/servicedesk/customer/portals', '_blank');
                break;

            case 'email':
                tab = window.open('mailto:support@firesurveypro.co.uk');
                tab.close();
                break;

            case 'phone':
                tab = window.open('tel:01482 425516');
                tab.close();
                break;
        }
    }

    public async resetTours()
    {
        await this.walkthroughService.reset().then((result) => {
            this.snackBar.open('Walkthroughs reset', 'Ok', {
                duration: 2000,
            });
        });
    }

    public throwTestError(): void
    {
        throw new Error('Sentry Test Error');
    }

    public get showDeveloperMenu(): boolean
    {
        const email = this.authService.user?.email?.toLowerCase() ?? '';

        return this.isSuperAdmin() || email.endsWith('@firesurveypro.co.uk');
    }

    private isSuperAdmin(): boolean
    {
        const userRoles = this.authService.user?.roles ?? [];

        if (userRoles.some((role: any) => typeof role === 'string' ? role === 'super-admin' : role?.name === 'super-admin')) {
            return true;
        }

        return Boolean(this.permissionsService.hasRole('super-admin'));
    }

    private getImpersonationErrorMessage(error: any, fallback: string): string
    {
        if (error?.status === 403) {
            return error?.error?.message ?? 'You are not allowed to perform this impersonation action.';
        }

        return error?.error?.message ?? fallback;
    }

    public ngOnDestroy(): void
    {
        if (this.marketingConsentPromptTimeoutId !== null) {
            window.clearTimeout(this.marketingConsentPromptTimeoutId);
        }

        this.featureFlagSubscriptions.unsubscribe();
        this.maintenanceSummarySubscription?.unsubscribe();
    }

    private scheduleMarketingConsentPrompt(): void
    {
        if (!this.authService.shouldScheduleMarketingConsentPrompt()) {
            return;
        }

        this.authService.markMarketingConsentPromptScheduled();
        this.marketingConsentPromptTimeoutId = window.setTimeout(() => {
            if (this.authService.hasAnsweredMarketingConsent()) {
                return;
            }

            this.dialog.open(MarketingConsentModalComponent, {
                width: '32rem',
                panelClass: 'mobile-width',
                disableClose: true,
            });
        }, 30000);
    }
}
